import { NetError } from './error'
import { RequestStats, getRangeValue } from './shared'

export class XhrLoader {
  _xhr = null
  _stats = null
  _timeoutTimer = null

  load (request) {
    const stats = this._stats = new RequestStats()
    clearTimeout(this._timeoutTimer)

    let {
      url,
      timeout, // ms
      responseType,
      onTimeout,
      responseFilter,
      onProgress,
      method,
      credentials,
      body,
      headers,
      range
    } = request

    let isTimeout = false

    return new Promise((resolve, reject) => {
      const xhr = this._xhr = new XMLHttpRequest()
      xhr.open(method || 'GET', url, true)
      xhr.responseType = responseType
      xhr.withCredentials = credentials === 'include' || credentials === 'same-origin'

      const rangeValue = getRangeValue(range)
      if (rangeValue) {
        headers = headers || {}
        headers.range = rangeValue
      }

      if (headers) {
        Object.keys(headers).forEach(k => {
          xhr.setRequestHeader(k, headers[k])
        })
      }

      if (timeout) {
        this._timeoutTimer = setTimeout(() => {
          isTimeout = true
          this.cancel()
          if (onTimeout) {
            const error = new NetError(url, request)
            error.isTimeout = true
            onTimeout(error)
          }
        }, timeout)
      }

      const startTime = performance.now()

      xhr.onerror = (event) => reject(event)
      xhr.onreadystatechange = () => {
        if (xhr.readyState >= 2) {
          clearTimeout(this._timeoutTimer)

          if (!stats.loadTime) {
            stats.loadTime = performance.now() - startTime
          }

          if (xhr.readyState === 4) {
            xhr.onreadystatechange = null
            stats.totalTime = performance.now() - startTime

            const status = xhr.status
            let response = {
              ok: status >= 200 && status < 300,
              status,
              statusText: xhr.statusText,
              url,
              headers: this._getHeaders(xhr),
              body: xhr.response
            }

            if (responseFilter) {
              response = responseFilter(url, response) || response
            }

            if (!response.ok) {
              return reject(new NetError(url, request, response, 'bad network response'))
            }

            const isArraybuffer = xhr.responseType === 'arraybuffer'
            let data = isArraybuffer ? new Uint8Array(xhr.response) : xhr.response

            let contentLength = parseInt(response.headers.get('content-length') || '0', 10)
            if (isNaN(contentLength)) contentLength = 0

            if (isArraybuffer && !contentLength) {
              contentLength = data.byteLength
            }

            if (stats.aborted) data = undefined

            if (onProgress) {
              onProgress(isArraybuffer ? data : undefined, true, response, stats, stats.totalTime)
            }

            resolve({
              data,
              response,
              stats,
              contentLength,
              age: response.headers.age && parseFloat(response.headers.age)
            })
          }
        }
      }

      xhr.send(body)
    }).catch((error) => {
      clearTimeout(this._timeoutTimer)
      if (stats.aborted && !isTimeout) return
      error = error instanceof NetError ? error : new NetError(url, request)
      error.isTimeout = isTimeout
      throw error
    })
  }

  cancel () {
    if (this._stats) this._stats.aborted = true

    if (this._xhr) {
      return this._xhr.cancel()
    }
  }

  static isSupported () {
    return typeof XMLHttpRequest !== 'undefined'
  }

  _getHeaders (xhr) {
    const headerLines = xhr.getAllResponseHeaders().trim().split('\r\n')
    const headers = {}
    for (const header of headerLines) {
      const parts = header.split(': ')
      headers[parts[0].toLowerCase()] = parts.slice(1).join(': ')
    }
    return headers
  }
}
